import React, { useState, useEffect } from 'react';
import { ShieldAlert, Eye, Ban, CheckCircle, Activity, Zap } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const severityConfig = {
  critical: { label: 'Critical', color: 'text-red-400 bg-red-400/10 border-red-400/20', dot: 'bg-red-400' },
  high: { label: 'High', color: 'text-orange-400 bg-orange-400/10 border-orange-400/20', dot: 'bg-orange-400' },
  medium: { label: 'Medium', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20', dot: 'bg-yellow-400' },
  low: { label: 'Low', color: 'text-gray-400 bg-gray-400/10 border-gray-400/20', dot: 'bg-gray-400' },
};

const statusConfig = {
  active: { label: 'Active', color: 'text-red-400' },
  monitoring: { label: 'Monitoring', color: 'text-yellow-400' },
  resolved: { label: 'Resolved', color: 'text-green-400' },
  suspended: { label: 'Suspended', color: 'text-gray-400' },
};

function RiskScoreBar({ score }) {
  const color = score >= 90 ? 'bg-red-500' : score >= 75 ? 'bg-orange-500' : score >= 60 ? 'bg-yellow-500' : 'bg-green-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className={`text-xs font-bold ${score >= 90 ? 'text-red-400' : score >= 75 ? 'text-orange-400' : score >= 60 ? 'text-yellow-400' : 'text-green-400'}`}>
        {score}
      </span>
    </div>
  );
}

export default function FraudDetection() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    loadFraudAlerts();
  }, []);

  const loadFraudAlerts = async () => {
    setLoading(true);
    try {
      // Fetch users with high activity or inactive status for review
      const { data, error } = await supabase?.from('user_profiles')?.select(`
          id,
          full_name,
          email,
          role,
          is_active,
          created_at,
          membership_tier,
          user_activities(count)
        `)?.order('created_at', { ascending: false })?.limit(30);

      if (!error && data?.length > 0) {
        // Map users to fraud alert format based on account characteristics
        const mapped = data?.map(user => {
          const activityCount = user?.user_activities?.[0]?.count || 0;
          const isNew = (Date.now() - new Date(user.created_at)?.getTime()) < 7 * 24 * 60 * 60 * 1000;
          const isInactive = !user?.is_active;

          let severity = 'low';
          let type = 'Account Review';
          let signals = [];
          let riskScore = 20;

          if (isInactive) {
            severity = 'high';
            type = 'Inactive Account';
            signals = ['Account deactivated', 'Requires review'];
            riskScore = 75;
          } else if (isNew && activityCount > 10) {
            severity = 'medium';
            type = 'High Activity New Account';
            signals = ['New account', 'High activity volume'];
            riskScore = 55;
          } else if (user?.role === 'admin') {
            severity = 'low';
            type = 'Admin Account';
            signals = ['Elevated privileges'];
            riskScore = 30;
          }

          return {
            id: user?.id,
            type,
            severity,
            user: user?.full_name || user?.email,
            userId: user?.id,
            description: `User account ${isInactive ? 'is currently deactivated' : 'is active'}. Membership: ${user?.membership_tier}. Activity count: ${activityCount}.`,
            detectedAt: user?.created_at,
            signals,
            status: isInactive ? 'monitoring' : 'active',
            riskScore,
          };
        })?.filter(a => a?.severity !== 'low' || a?.status === 'monitoring');

        setAlerts(mapped?.length > 0 ? mapped : []);
      }
    } catch (err) {
      console.error('Error loading fraud alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = alerts?.filter(a => severityFilter === 'all' || a?.severity === severityFilter);
  const activeThreats = alerts?.filter(a => a?.status === 'active')?.length;
  const criticalCount = alerts?.filter(a => a?.severity === 'critical' && a?.status !== 'resolved')?.length;

  const handleAction = async (id, action) => {
    setActionLoading(id);
    try {
      if (action === 'ban') {
        const { error } = await supabase?.from('user_profiles')?.update({ is_active: false, updated_at: new Date()?.toISOString() })?.eq('id', id);
        if (!error) {
          setAlerts(prev => prev?.map(a => a?.id === id ? { ...a, status: 'suspended' } : a));
          setSelected(null);
        }
      } else if (action === 'monitor') {
        setAlerts(prev => prev?.map(a => a?.id === id ? { ...a, status: 'monitoring' } : a));
      } else if (action === 'investigate') {
        setAlerts(prev => prev?.map(a => a?.id === id ? { ...a, status: 'monitoring' } : a));
      } else if (action === 'clear') {
        setAlerts(prev => prev?.map(a => a?.id === id ? { ...a, status: 'resolved' } : a));
        setSelected(null);
      }
    } catch (err) {
      console.error('Action error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[1,2,3]?.map(i => <div key={i} className="h-24 bg-gray-800 rounded-xl border border-gray-700" />)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Alert Banner */}
      {criticalCount > 0 && (
        <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse flex-shrink-0" />
          <ShieldAlert size={16} className="text-red-400 flex-shrink-0" />
          <span className="text-sm text-red-300 font-medium">{criticalCount} critical alert{criticalCount > 1 ? 's' : ''} require immediate action</span>
        </div>
      )}
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Active', value: activeThreats, icon: Zap, color: 'text-red-400' },
          { label: 'Critical', value: alerts?.filter(a => a?.severity === 'critical')?.length, icon: ShieldAlert, color: 'text-orange-400' },
          { label: 'Monitoring', value: alerts?.filter(a => a?.status === 'monitoring')?.length, icon: Eye, color: 'text-blue-400' },
          { label: 'Resolved', value: alerts?.filter(a => a?.status === 'resolved')?.length, icon: CheckCircle, color: 'text-green-400' },
        ]?.map(s => (
          <div key={s?.label} className="bg-gray-800 border border-gray-700 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">{s?.label}</span>
              <s.icon size={14} className={s?.color} />
            </div>
            <div className={`text-2xl font-bold ${s?.color}`}>{s?.value}</div>
          </div>
        ))}
      </div>
      {/* Severity Filter */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'critical', 'high', 'medium']?.map(sev => (
          <button
            key={sev}
            onClick={() => setSeverityFilter(sev)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              severityFilter === sev
                ? 'bg-blue-600/30 border border-blue-500/40 text-blue-300' :'bg-gray-800 border border-gray-700 text-gray-400 hover:border-gray-600'
            }`}
          >
            {sev === 'all' ? 'All Alerts' : sev?.charAt(0)?.toUpperCase() + sev?.slice(1)}
          </button>
        ))}
      </div>
      {/* Alert Cards */}
      <div className="space-y-3">
        {filtered?.map(alert => (
          <div
            key={alert?.id}
            className={`bg-gray-800 border rounded-xl p-4 cursor-pointer transition-all hover:border-gray-600 ${
              selected?.id === alert?.id ? 'border-blue-500/40 bg-blue-500/5' : 'border-gray-700'
            }`}
            onClick={() => setSelected(selected?.id === alert?.id ? null : alert)}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${severityConfig?.[alert?.severity]?.dot} ${alert?.status === 'active' ? 'animate-pulse' : ''}`} />
                  <span className="font-semibold text-white text-sm">{alert?.type}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${severityConfig?.[alert?.severity]?.color}`}>
                    {severityConfig?.[alert?.severity]?.label}
                  </span>
                  <span className={`text-xs font-medium ${statusConfig?.[alert?.status]?.color || 'text-gray-400'}`}>
                    {statusConfig?.[alert?.status]?.label || alert?.status}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mb-2 line-clamp-2">{alert?.description}</p>
                <div className="flex items-center gap-3 text-xs text-gray-500">
                  <span>User: <span className="text-blue-400">{alert?.user}</span></span>
                  <span>{new Date(alert.detectedAt)?.toLocaleDateString()}</span>
                </div>
              </div>
              <div className="w-24 flex-shrink-0">
                <div className="text-xs text-gray-500 mb-1 text-right">Risk Score</div>
                <RiskScoreBar score={alert?.riskScore} />
              </div>
            </div>

            {alert?.signals?.length > 0 && (
              <div className="flex gap-1.5 flex-wrap mt-3">
                {alert?.signals?.map(signal => (
                  <span key={signal} className="px-2 py-0.5 bg-gray-700/60 border border-gray-600/50 rounded-full text-xs text-gray-300">
                    {signal}
                  </span>
                ))}
              </div>
            )}

            {selected?.id === alert?.id && alert?.status !== 'resolved' && alert?.status !== 'suspended' && (
              <div className="flex gap-2 flex-wrap mt-4 pt-4 border-t border-gray-700" onClick={e => e?.stopPropagation()}>
                <button
                  onClick={() => handleAction(alert?.id, 'investigate')}
                  disabled={actionLoading === alert?.id}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600/20 border border-blue-600/30 text-blue-400 rounded-lg text-xs hover:bg-blue-600/30 transition-colors disabled:opacity-50"
                >
                  <Eye size={12} /> Investigate
                </button>
                <button
                  onClick={() => handleAction(alert?.id, 'monitor')}
                  disabled={actionLoading === alert?.id}
                  className="flex items-center gap-2 px-3 py-2 bg-yellow-600/20 border border-yellow-600/30 text-yellow-400 rounded-lg text-xs hover:bg-yellow-600/30 transition-colors disabled:opacity-50"
                >
                  <Activity size={12} /> Monitor
                </button>
                <button
                  onClick={() => handleAction(alert?.id, 'ban')}
                  disabled={actionLoading === alert?.id}
                  className="flex items-center gap-2 px-3 py-2 bg-red-600/20 border border-red-600/30 text-red-400 rounded-lg text-xs hover:bg-red-600/30 transition-colors disabled:opacity-50"
                >
                  <Ban size={12} /> Suspend User
                </button>
                <button
                  onClick={() => handleAction(alert?.id, 'clear')}
                  disabled={actionLoading === alert?.id}
                  className="flex items-center gap-2 px-3 py-2 bg-green-600/20 border border-green-600/30 text-green-400 rounded-lg text-xs hover:bg-green-600/30 transition-colors disabled:opacity-50"
                >
                  <CheckCircle size={12} /> Clear Alert
                </button>
              </div>
            )}
          </div>
        ))}
        {filtered?.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <ShieldAlert size={32} className="mx-auto mb-3 opacity-30" />
            <p>No alerts in this category</p>
          </div>
        )}
      </div>
    </div>
  );
}
